import { useMemo } from "react";
import { motion } from "framer-motion";
import { Share2, Copy, Sparkles, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { CARD_SUITS, FACE_CARDS, JOKERS } from "@/data/cards";
import { sutras } from "@/data/sutras";
import { useCopy } from "@/lib/copyVariant";

const allCards = [
  ...CARD_SUITS.diamonds, ...CARD_SUITS.hearts,
  ...CARD_SUITS.spades, ...CARD_SUITS.clubs,
  ...FACE_CARDS, ...JOKERS,
];

export default function CardOfDay() {
  const { c } = useCopy();
  const { card, sutra } = useMemo(() => {
    const now = new Date();
    const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
    const baseSutra = sutras[dayOfYear % sutras.length];
    // Variant may override the description for the current day's sutra id.
    let overrides = {};
    try { overrides = c("cardOfDayOverrides") || {}; } catch (_) { /* noop */ }
    const finalSutra = overrides[baseSutra.id]
      ? { ...baseSutra, description: overrides[baseSutra.id] }
      : baseSutra;
    return {
      card: allCards[dayOfYear % allCards.length],
      sutra: finalSutra,
    };
  }, [c]);

  // Canonical site URL - share links always point to calmasutras.com regardless of
  // which environment (preview / production) the visitor is currently on.
  const SHARE_URL = "https://calmasutras.com/";

  const shareText = `Today's Calmasutras: "${sutra.title}". ${sutra.description.slice(0, 100)}...`;

  const copyLink = () => {
    navigator.clipboard.writeText(`${shareText}\n${SHARE_URL}`);
    toast.success("Copied to clipboard!");
  };
  const shareX = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(SHARE_URL)}`);
  };
  const shareLinkedIn = () => {
    // LinkedIn's sharing dialog only reads the URL's OG tags - text/title params are deprecated.
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}`);
  };

  const SAGE_PATTERN = "https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/edluaasr_5.png";
  const SWIRL_PATTERN = "https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/co7ekdv0_2.png";

  return (
    <section className="py-16 sm:py-24 md:py-32 px-5 md:px-10 relative overflow-hidden" data-testid="card-of-day-section">
      {/* Sage brand color background with layered patterns */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #8EACA6 0%, #A3BBB5 50%, #96B2AC 100%)" }} />
      <img src={SAGE_PATTERN} alt="" aria-hidden="true" loading="lazy" decoding="async" width="1400" height="900" className="absolute inset-0 w-full h-full object-cover opacity-[0.35] pointer-events-none mix-blend-overlay" />
      <img src={SWIRL_PATTERN} alt="" aria-hidden="true" loading="lazy" decoding="async" width="1400" height="900" className="absolute inset-0 w-full h-full object-cover opacity-[0.18] pointer-events-none mix-blend-multiply" />
      {/* Soft vignette to focus the card panel */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 35%, rgba(8,8,10,0.35) 100%)" }} />

      <div className="max-w-[1400px] mx-auto relative z-10">
        <motion.div className="bg-white/85 backdrop-blur-md rounded-2xl sm:rounded-3xl overflow-hidden border border-white/60 shadow-xl shadow-[#464646]/10"
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <div className="p-5 sm:p-8 md:p-12">
            <div className="flex items-center gap-2 mb-6 sm:mb-8">
              <Sparkles size={16} className="text-[#7a9d83]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-[#7a9d83] font-semibold">Card of the Day</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] lg:grid-cols-[340px_1fr] gap-6 sm:gap-8 md:gap-12 items-center">
              {/* 3D Card */}
              <motion.div className="card-3d mx-auto md:mx-0 w-48 sm:w-56 md:w-full"
                whileHover={{ rotateY: 0, rotateX: 0 }}
                whileTap={{ rotateY: 0, rotateX: 0, scale: 1.05 }}>
                <img src={card.url} alt={card.name} loading="lazy" decoding="async" width="320" height="448"
                  className="w-full rounded-xl sm:rounded-2xl shadow-2xl shadow-[#464646]/20" data-testid="card-of-day-image" />
              </motion.div>

              {/* Sutra info */}
              <div>
                <p className="text-[10px] sm:text-xs text-[#96aeb4] font-semibold uppercase tracking-[0.3em] mb-2">{card.name} &middot; Sutra {sutra.id}</p>
                <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#464646] font-bold mb-3 sm:mb-4 leading-snug" data-testid="card-of-day-title">
                  {sutra.title}
                </h3>
                <p className="text-sm sm:text-base text-[#464646]/70 leading-relaxed mb-6 sm:mb-8" data-testid="card-of-day-desc">
                  {sutra.description}
                </p>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <button onClick={copyLink} data-testid="share-copy-btn"
                    className="inline-flex items-center gap-2 bg-[#96aeb4]/15 hover:bg-[#96aeb4]/25 border border-[#96aeb4]/30 rounded-full px-4 py-2 text-xs sm:text-sm text-[#464646]/80 hover:text-[#464646] transition-all">
                    <Copy size={14} /> Copy Link
                  </button>
                  <button onClick={shareX} data-testid="share-x-btn"
                    className="inline-flex items-center gap-2 bg-[#96aeb4]/15 hover:bg-[#96aeb4]/25 border border-[#96aeb4]/30 rounded-full px-4 py-2 text-xs sm:text-sm text-[#464646]/80 hover:text-[#464646] transition-all">
                    <Share2 size={14} /> Share on X
                  </button>
                  <button onClick={shareLinkedIn} data-testid="share-linkedin-btn"
                    className="inline-flex items-center gap-2 bg-[#96aeb4]/15 hover:bg-[#96aeb4]/25 border border-[#96aeb4]/30 rounded-full px-4 py-2 text-xs sm:text-sm text-[#464646]/80 hover:text-[#464646] transition-all">
                    <Linkedin size={14} /> Share on LinkedIn
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
